import React, { useState, useEffect, useCallback } from "react";
import { Tag, Button, Table, Space, Tooltip, Popconfirm, message, Modal, Checkbox } from "antd";
import { QuestionCircleOutlined, MinusSquareOutlined, PlusSquareOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { ThunderboltOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";

import EditGroupPopover from "./EditGroupPopover";
import AddGroupPopover from "./AddGroupPopover";
import ActionButtonGroups from "../ActionButtonGroups";
import UpdateLeverageButton from "../UpdateLeverageButton";
import SetLeverageForAllButton from "./SetLeverageForAllButton";
import { buildTradeExecutionParams } from "../TradeModal";
import EngineChoice from "../EngineChoice";
import { ENGINE, isEngineChoiceShown, withEngine } from "../../utils/engine";
import {
  UNGROUPED_NAME,
  generateNewRelicLink,
  formatReadableNumber,
  isSubAccount,
  CONTROL_ACTION_TYPE,
  naturalSortSessions,
  TMUX_OPERATION_DELAY,
  INSTRUMENT,
  isArbitrage,
  determineTradeTypeByExchangeInstrument,
  TRADE_STATUS,
  isUsdsFuture,
  isCoinFuture,
} from "../../utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import styles from "./index.module.scss";
import Service, { ElAdminEventLogService } from "../../services";

dayjs.extend(relativeTime);
// 对于同一sessionName的tradeStatus只留下最近更新的一个

const GroupTable = ({
  setSideBarPage,
  group,
  allTablesExpanded,
  tradeStatusList,
  setActionModalDetails,
  popoverAllAccounts,
  popoverUngroupedAccounts,
  popoverSelectedAccounts,
  onRemoveGroup,
  onUpdateSelectedAccounts,
  onConfirmEditGroup,
  onConfirmAddGroup,
}) => {
  const history = useHistory();
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [isAllExpanded, setIsAllExpanded] = useState(allTablesExpanded);
  const [storedAllTablesExpanded, setStoredAllTablesExpanded] = useState();
  const [tradeStatusData, setTradeStatusData] = useState();
  const [isEmptyModalVisible, setIsEmptyModalVisible] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState(new Set());
  const [groupedSessions, setGroupedSessions] = useState({});
  const [pauseAllEngine, setPauseAllEngine] = useState(ENGINE.legacy);
  const [emptyAllEngine, setEmptyAllEngine] = useState(ENGINE.legacy);

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const getCurrentSiblings = (parentAccountId, exchangeId) => {
    return (
      tradeStatusData.siblingTradeStatusGroups.find((group) =>
        group.some((status) => status.exchangeId === exchangeId && status.account === parentAccountId)
      ) || []
    );
  };

  const toggleExpandAllRows = () => {
    if (!tradeStatusData) return;
    if (isAllExpanded) {
      setExpandedRowKeys([]);
    } else {
      setExpandedRowKeys(tradeStatusData.parentTradeStatuses.map((status) => status.key));
    }
    setIsAllExpanded(!isAllExpanded);
  };

  const tradeAll = (siblingAccounts) => {
    if (!siblingAccounts.length) {
      message.error("No accounts available for trading");
      return;
    }
    if (!siblingAccounts.every((account) => account.permissions?.includes("CONTROL"))) {
      message.error("You don't have the permission to trade all accounts");
      return;
    }
    const baseAccount = siblingAccounts[0];
    const tradeModelDetails = {
      ...baseAccount,
      actionType: CONTROL_ACTION_TYPE["Start Trade"],
      siblingAccountStatuses: siblingAccounts,
      isTradeAll: true,
    };

    setActionModalDetails(tradeModelDetails);
  };

  const pauseAll = async (siblingAccounts, engine) => {
    for (const account of siblingAccounts) {
      if (!account.permissions || !account.permissions.includes("CONTROL")) {
        message.error("You don't have the permission to pause all accounts");
        return;
      }
      if (account.status === TRADE_STATUS.trading) {
        try {
          const pauseDetails = { ...account, actionType: CONTROL_ACTION_TYPE["Pause Trade"] };
          const result = await Service.stopTradeExecution(pauseDetails, engine);
          await ElAdminEventLogService.saveTradeActionEvent(pauseDetails);
          message.success(`Paused: ${result}`);
          await delay(TMUX_OPERATION_DELAY);
        } catch (error) {
          message.error(`Failed to pause ${account.account}: ${error.message}`);
          break;
        }
      }
    }
  };

  const emptyAll = useCallback(async (siblingAccounts, engine) => {
    for (const account of siblingAccounts) {
      if (!account.permissions || !account.permissions.includes("CONTROL")) {
        message.error("You don't have the permission to empty all accounts");
        return;
      }
      if (account.status === TRADE_STATUS.trading) {
        try {
          if (!account.sessionName) {
            throw new Error("Can't find sessionName for this trade session");
          }

          // Use shared helper function - ensures consistency with individual close position
          const emptyDetails = buildTradeExecutionParams(account);

          const result = await Service.closePositionTradeExecution(withEngine(emptyDetails, engine));
          await ElAdminEventLogService.saveTradeActionEvent({ ...account, actionType: CONTROL_ACTION_TYPE["Close Position"] });
          message.success(`${result}`);
          await delay(TMUX_OPERATION_DELAY);
        } catch (error) {
          message.error(`Failed to empty ${account.account}: ${error.message}`);
          console.error(error);
          break;
        }
      }
    }
  }, []);

  const showEmptyModal = useCallback((siblingAccounts) => {
    // Filter to only trading sessions from usds_future and coin_future (no arbitrage)
    const tradingSessions = siblingAccounts.filter(
      (account) =>
        account.status === TRADE_STATUS.trading &&
        !isArbitrage(account.exchange) &&
        (isUsdsFuture(account.exchange) || isCoinFuture(account.exchange))
    );

    // Group by exchange
    const grouped = {};
    tradingSessions.forEach((session) => {
      if (!grouped[session.exchange]) {
        grouped[session.exchange] = [];
      }
      grouped[session.exchange].push(session);
    });

    setGroupedSessions(grouped);
    setSelectedSessions(new Set());
    setEmptyAllEngine(ENGINE.legacy);
    setIsEmptyModalVisible(true);
  }, []);

  const handleEmptyModalCancel = useCallback(() => {
    setIsEmptyModalVisible(false);
  }, []);

  const handleEmptyModalOk = useCallback(async () => {
    if (selectedSessions.size === 0) {
      message.error("Please select at least one session");
      return;
    }

    // Get all sessions from groupedSessions and filter to only selected ones
    const allSessions = Object.values(groupedSessions).flat();
    const selectedAccounts = allSessions.filter((session) => selectedSessions.has(session.key || session.sessionName));

    setIsEmptyModalVisible(false);
    await emptyAll(selectedAccounts, emptyAllEngine);
  }, [selectedSessions, groupedSessions, emptyAll, emptyAllEngine]);

  const handleSessionToggle = useCallback((sessionKey) => {
    setSelectedSessions((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sessionKey)) {
        newSet.delete(sessionKey);
      } else {
        newSet.add(sessionKey);
      }
      return newSet;
    });
  }, []);

  const handleExchangeToggle = useCallback(
    (exchange) => {
      const sessionsInExchange = groupedSessions[exchange] || [];
      const sessionKeys = sessionsInExchange.map((s) => s.key || s.sessionName);
      const allSelected = sessionKeys.every((key) => selectedSessions.has(key));

      setSelectedSessions((prev) => {
        const newSet = new Set(prev);
        if (allSelected) {
          // Deselect all
          sessionKeys.forEach((key) => newSet.delete(key));
        } else {
          // Select all
          sessionKeys.forEach((key) => newSet.add(key));
        }
        return newSet;
      });
    },
    [groupedSessions, selectedSessions]
  );

  useEffect(() => {
    const filteredTradeStatusList = filterTradeStatusList(tradeStatusList);
    const parentTradeStatuses = filteredTradeStatusList.filter((tradeStatus) => !isSubAccount(tradeStatus));
    // sibling accounts are accounts using the same api keys.
    const siblingTradeStatusGroups = parentTradeStatuses.map((parentTradeStatus) =>
      filteredTradeStatusList.filter(
        (tradeStatus) =>
          tradeStatus.exchange.split("_")[0] === parentTradeStatus.exchange.split("_")[0] &&
          (tradeStatus.parentAccount === parentTradeStatus.account || tradeStatus.account === parentTradeStatus.account)
      )
    );
    setTradeStatusData({ filteredTradeStatusList, parentTradeStatuses, siblingTradeStatusGroups });
  }, [tradeStatusList]);

  useEffect(() => {
    if (!tradeStatusData || allTablesExpanded === storedAllTablesExpanded) return;

    if (allTablesExpanded) {
      setIsAllExpanded(true);
      setExpandedRowKeys(tradeStatusData.parentTradeStatuses.map((status) => status.key));
    } else {
      setIsAllExpanded(false);
      setExpandedRowKeys([]);
    }
    setStoredAllTablesExpanded(allTablesExpanded);
  }, [allTablesExpanded, storedAllTablesExpanded, tradeStatusData]);

  if (!tradeStatusData) {
    return null; // let the effects run first
  }

  const expandedSiblingTradeStatusGroupRender = (record) => {
    const columns = [
      {
        title: "Exchange Instrument",
        dataIndex: "exchange",
        key: "exchange",
        render: (text, record) => {
          // For arbitrage accounts, add tooltip explaining how it works
          if (isArbitrage(record.exchange)) {
            return (
              <Tooltip
                title="Arbitrage Trading: Supports PM mode only. USDM and arbitrage accounts alternate execution every minute until reaching the target amount. Target Amount = absolute target asset amount. Future can be negative (short) or positive (long). Reserved Amount = reserved margin asset amount. Total margin = Target Amount + Reserved Amount. USDM effective leverage = (USDM leverage - 1) × leverageScaleFactor."
                overlayStyle={{ maxWidth: "1200px" }}
                overlayInnerStyle={{
                  padding: "15px",
                  width: "600px",
                  maxWidth: "100vw",
                }}
              >
                <span style={{ borderBottom: "1px dotted #ccc", cursor: "help" }}>{text}</span>
              </Tooltip>
            );
          }
          return text;
        },
      },
      {
        title: "Account",
        dataIndex: "account",
        key: "account",
      },
      {
        title: "Symbol",
        dataIndex: "symbol",
        key: "symbol",
      },
      {
        title: "Balance",
        dataIndex: "marginBalance",
        key: "marginBalance",
        render: (text, record) => {
          // For binance_funding_rate_arbitrage, display arbitrage target amount (min of spot and |future|)
          if (isArbitrage(record.exchange)) {
            let arbitrageTargetAmt = 0;
            if (record.arbitrageParams) {
              const parsedParams = JSON.parse(record.arbitrageParams);
              const spotTargetAmt = parsedParams.spotTargetAmt || 0;
              const futureTargetAmt = parsedParams.futureTargetAmt || 0;
              const absFuture = Math.abs(futureTargetAmt);
              // For hedged positions (spot + short future), show min; for long future, show total exposure
              arbitrageTargetAmt = futureTargetAmt >= 0 ? spotTargetAmt + absFuture : Math.min(spotTargetAmt, absFuture);
            }
            return <Tooltip title="Hedged position size (short future) or total exposure (long future)">{arbitrageTargetAmt}</Tooltip>;
          }
          return text;
        },
      },
      {
        title: "Allocation",
        dataIndex: "balanceAllocationRatio",
        key: "balanceAllocationRatio",
        render: (text, record) => {
          // For binance_funding_rate_arbitrage, always display 100%
          if (isArbitrage(record.exchange)) {
            return "100%";
          }

          const balanceAllocationRatio = record.parsedConfigs.balanceAllocationRatio;
          if (typeof balanceAllocationRatio === "number") {
            return `${record.parsedConfigs.balanceAllocationRatio}%`;
          }
          return "100%";
        },
      },
      {
        title: "Position",
        dataIndex: "currentState",
        key: "currentState",
        render: (text, record) => {
          // For binance_funding_rate_arbitrage, display arbitrageMarginAmt and arbitrageUsdmAmt
          if (isArbitrage(record.exchange)) {
            const marginAmt = record.arbitrageMarginAmt ?? 0;
            const usdmAmt = record.arbitrageUsdmAmt ?? 0;
            return (
              <Tooltip title="Positive is margin balance, negative is short contract, positive futures = long contract. Once target position is reached, user can buy/sell margin asset and arbitrage won't enforce the balance, just monitor actual balance.">
                <span>
                  {marginAmt} / {usdmAmt}
                </span>
              </Tooltip>
            );
          }
          return text;
        },
      },
      {
        title: "Info",
        key: "info",
        width: 60,
        render: (_, record) => {
          if (record.info) {
            return (
              <Tooltip
                title={<pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{record.info}</pre>}
                overlayStyle={{ maxWidth: "1200px" }}
                overlayInnerStyle={{
                  padding: "15px",
                  // color: 'rgba(0, 0, 0, 0.85)',
                  width: "600px",
                  maxWidth: "100vw",
                  // boxShadow: '0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 6px 16px 0 rgba(0, 0, 0, 0.08)'
                }}
              >
                <InfoCircleOutlined style={{ cursor: "pointer" }} />
              </Tooltip>
            );
          }
          return null;
        },
      },
      {
        title: () => (
          <>
            Leverage
            <Tooltip
              title={`EffectiveLeverage = Leverage * ScaleFactor\nScaleFactor: a number between [0.5, 0.7]. It is used to leave a buffer in balance to ensure the trading can be executed properly during volatile times for the trading pair. We use greater ScaleFactors for less volatile pairs, and vice versa`}
              overlayInnerStyle={{ width: 300 }}
              color="#78909c"
            >
              (<QuestionCircleOutlined />)
            </Tooltip>
          </>
        ),
        dataIndex: "leverage",
        key: "leverage",
        render: (_, record) => {
          return (
            <>
              {record.leverage}({record.practicalLeverage > 0 ? record.practicalLeverage.toFixed(1) : record.practicalLeverage}
              )
              <UpdateLeverageButton actionDetails={record} />
            </>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (_, record) => {
          // const color = record.errorMessage ? "red" : tradeStatusToColor[record.status];
          return (
            <>
              <Tag color={tradeStatusToColor[record.status]}>{record.status + (record.errorMessage ? "(error)" : "")}</Tag>
              {record.errorMessage && <div>{record.errorMessage}</div>}
            </>
          );
        },
      },
      {
        title: "updatedAt",
        dataIndex: "updatedAt",
        key: "updatedAt",
        render: (_, record) => {
          return <>{dayjs(record.updatedAt).fromNow()}</>;
        },
      },
      {
        title: "Actions",
        dataIndex: "actions",
        key: "actions",
        width: 100,
        render: (_, record) => (
          <div className={styles.accountGroupBtn}>
            <Space>
              {record.permissions && record.permissions.includes("CONTROL") && (
                <ActionButtonGroups actionDetails={record} setActionDetails={setActionModalDetails} />
              )}

              <Button
                type="link"
                onClick={() => {
                  setSideBarPage("accountDetails"); // update state of sidebar
                  history.push(`/main/accountDetails/${record.key}`);
                }}
              >
                Details
              </Button>

              <Button type="link" href={generateNewRelicLink(record.exchange, record.account, record.symbol)} target="_blank">
                Log
              </Button>
            </Space>
          </div>
        ),
      },
    ];
    const dataSource = tradeStatusData.siblingTradeStatusGroups.find((group) =>
      group.find((tradeStatus) => tradeStatus.exchange.includes(record.exchangeId) && tradeStatus.account === record.account)
    );
    return <Table dataSource={dataSource} columns={columns} pagination={false} />;
  };

  const columns = [
    {
      title: "Exchange",
      dataIndex: "exchangeId",
      key: "exchangeId",
      width: "5%",
    },
    {
      title: "Account",
      dataIndex: "account",
      key: "account",
      width: "20%",
    },
    {
      title: "Total Balance",
      dataIndex: "totalBalance",
      key: "totalBalance",
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: "35%",
      render: (_, record) => {
        const siblingAccounts = getCurrentSiblings(record.account, record.exchangeId);
        const hasControlPermission = siblingAccounts.every((account) => account.permissions.includes("CONTROL"));
        if (!hasControlPermission) {
          return null; // Do not render anything if any sibling account does not have "CONTROL" permission
        }
        return (
          <Space>
            <>
              <Button type="default" icon={<ThunderboltOutlined />} onClick={() => tradeAll(siblingAccounts)}>
                Trade All
              </Button>
              <Popconfirm
                title="Are you sure you want to pause all trading sessions of this account?"
                description={isEngineChoiceShown() ? <EngineChoice engine={pauseAllEngine} onChange={setPauseAllEngine} /> : undefined}
                onOpenChange={(open) => open && setPauseAllEngine(ENGINE.legacy)}
                onConfirm={() => pauseAll(siblingAccounts, pauseAllEngine)}
                okText="Yes"
                cancelText="No"
              >
                <Button>Pause All</Button>
              </Popconfirm>
              <Button onClick={() => showEmptyModal(siblingAccounts)}>Empty All</Button>
              <SetLeverageForAllButton accounts={siblingAccounts} />
              <Tooltip title="These actions apply to all sessions of this parent account">
                <InfoCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)" }} />
              </Tooltip>
            </>
          </Space>
        );
      },
    },
  ];
  const totalBalanceInGroup = { usd: 0, coin: {} };
  const dataSource = tradeStatusData.parentTradeStatuses.map((parentTradeStatus) => {
    const siblingTradeStatuses = tradeStatusData.siblingTradeStatusGroups
      .find((group) => group.find((tradeStatus) => tradeStatus === parentTradeStatus))
      ?.sort(naturalSortSessions);
    // Filter out arbitrage sessions from balance calculations
    const validSiblings = siblingTradeStatuses.filter((status) => {
      const type = determineTradeTypeByExchangeInstrument(status.exchange);
      return (
        [INSTRUMENT.usds_future, INSTRUMENT.funding_rate_arbitrage].includes(type) &&
        status.marginBalance !== null &&
        !isArbitrage(status.exchange)
      );
    });
    let usdBalance;
    if (validSiblings.length > 0) {
      const tradingSiblings = validSiblings.filter((status) => status.status === "trading");
      const nonTradingSiblings = validSiblings.filter((status) => status.status !== "trading" && !status.exchange.includes("arbitrage"));
      const totalTradingAllocationRatio = tradingSiblings.reduce(
        (total, sibling) => total + (sibling.parsedConfigs.balanceAllocationRatio ?? 100),
        0
      );
      const totalTradingMarginBalance = tradingSiblings.reduce((total, sibling) => total + (sibling.marginBalance || 0), 0);
      const nonTradingAllocationRatio = nonTradingSiblings.reduce(
        (total, sibling) => total + (sibling.parsedConfigs.balanceAllocationRatio ?? 100),
        0
      );
      if (totalTradingAllocationRatio > 0) {
        usdBalance = totalTradingMarginBalance / (totalTradingAllocationRatio / 100);
      } else {
        const remainingAllocationRatio = 100 - totalTradingAllocationRatio;
        const nonTradingBalanceSum = nonTradingSiblings.reduce((total, sibling) => total + (sibling.marginBalance || 0), 0);
        const adjustedNonTradingBalance = (nonTradingBalanceSum / nonTradingAllocationRatio) * remainingAllocationRatio;
        usdBalance = totalTradingMarginBalance + adjustedNonTradingBalance;
      }
    } else {
      // Only include parent balance if it's not an arbitrage account
      usdBalance = isArbitrage(parentTradeStatus.exchange)
        ? 0
        : parentTradeStatus.marginBalance / ((parentTradeStatus.parsedConfigs.balanceAllocationRatio ?? 100) / 100);
    }
    // Filter out arbitrage sessions from coin balances as well
    const coinBalances =
      siblingTradeStatuses
        ?.filter((tradeStatus) => tradeStatus.type === "COIN_FUTURE" && !isArbitrage(tradeStatus.exchange))
        ?.filter(({ marginBalance }) => marginBalance)
        ?.map(({ symbol, marginBalance }) => ({ symbol: symbol.split("/")[0], marginBalance })) ?? [];
    const coinBalancesStr = coinBalances
      ?.map(({ symbol, marginBalance }) => `${symbol}: ${formatReadableNumber(marginBalance)}`)
      ?.join(", ");
    const totalBalance = `USD: $${formatReadableNumber(usdBalance)} ${coinBalancesStr}`;

    totalBalanceInGroup.usd += usdBalance;
    coinBalances.forEach(({ symbol, marginBalance }) => {
      totalBalanceInGroup.coin[symbol] = (totalBalanceInGroup.coin[symbol] || 0) + marginBalance;
    });

    return {
      key: parentTradeStatus.key,
      exchangeId: parentTradeStatus.exchangeId,
      account: parentTradeStatus.account,
      totalBalance: totalBalance,
    };
  });
  const totalBalanceInGroupStr = `USD: $${formatReadableNumber(totalBalanceInGroup.usd)} ${Object.entries(totalBalanceInGroup.coin)
    .map(([symbol, balance]) => `${symbol}: ${formatReadableNumber(balance)}`)
    .join(", ")}`;

  return (
    <div className={styles.accountGroup} key={group}>
      <div className={styles.accountHeader}>
        <div className={styles.title}>
          <Space>{group}</Space>
          <Space>
            <Button
              onClick={toggleExpandAllRows}
              icon={isAllExpanded ? <MinusSquareOutlined /> : <PlusSquareOutlined />}
              type="link"
            ></Button>
          </Space>
        </div>
        <div className={styles.editGroup + " pointer"}>
          {/* <Space>{group === UNGROUPED_NAME ? addGroupPopOver() : editGroupPopOver(group)}</Space> */}
          <Space>
            {group === UNGROUPED_NAME ? (
              <AddGroupPopover
                popoverUngroupedAccounts={popoverUngroupedAccounts}
                selectedAccounts={popoverSelectedAccounts}
                onUpdateSelectedAccounts={onUpdateSelectedAccounts}
                onConfirmAddGroup={onConfirmAddGroup}
              />
            ) : (
              <EditGroupPopover
                group={group}
                allAccounts={popoverAllAccounts}
                selectedAccounts={popoverSelectedAccounts}
                onRemoveGroup={onRemoveGroup}
                onUpdateSelectedAccounts={onUpdateSelectedAccounts}
                onConfirmEditGroup={onConfirmEditGroup}
              />
            )}
          </Space>
        </div>
        <div>Total Balance in group: ${totalBalanceInGroupStr}</div>
      </div>
      <Table
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        expandable={{
          expandedRowRender: expandedSiblingTradeStatusGroupRender,
          expandedRowKeys: expandedRowKeys,
          defaultExpandAllRows: false,
          onExpand: (expanded, record) => {
            const keys = expanded ? [...expandedRowKeys, record.key] : expandedRowKeys.filter((k) => k !== record.key);
            setExpandedRowKeys(keys);
            setIsAllExpanded(keys.length === tradeStatusData.parentTradeStatuses.length);
          },
        }}
      />
      <EmptyAllModal
        isVisible={isEmptyModalVisible}
        onCancel={handleEmptyModalCancel}
        onOk={handleEmptyModalOk}
        groupedSessions={groupedSessions}
        selectedSessions={selectedSessions}
        onSessionToggle={handleSessionToggle}
        onExchangeToggle={handleExchangeToggle}
        engine={emptyAllEngine}
        onEngineChange={setEmptyAllEngine}
      />
    </div>
  );
};

const EmptyAllModal = ({
  isVisible,
  onCancel,
  onOk,
  groupedSessions,
  selectedSessions,
  onSessionToggle,
  onExchangeToggle,
  engine,
  onEngineChange,
}) => {
  const exchanges = Object.keys(groupedSessions).sort();

  return (
    <Modal
      title="Select Sessions to Empty"
      open={isVisible}
      onOk={onOk}
      onCancel={onCancel}
      destroyOnClose={false}
      maskClosable={true}
      width={600}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {isEngineChoiceShown() && <EngineChoice engine={engine} onChange={onEngineChange} />}
        {exchanges.map((exchange) => {
          const sessions = groupedSessions[exchange] || [];
          const sessionKeys = sessions.map((s) => s.key || s.sessionName);
          const selectedCount = sessionKeys.filter((key) => selectedSessions.has(key)).length;
          const allSelected = selectedCount === sessionKeys.length;
          const someSelected = selectedCount > 0 && selectedCount < sessionKeys.length;

          return (
            <div key={exchange} style={{ marginBottom: "8px" }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => onExchangeToggle(exchange)}
                style={{ fontWeight: "bold", marginBottom: "8px" }}
              >
                {exchange} ({selectedCount}/{sessionKeys.length})
              </Checkbox>
              <div style={{ marginLeft: "24px", display: "flex", flexDirection: "column", gap: "4px" }}>
                {sessions.map((session) => {
                  const sessionKey = session.key || session.sessionName;
                  return (
                    <Checkbox key={sessionKey} checked={selectedSessions.has(sessionKey)} onChange={() => onSessionToggle(sessionKey)}>
                      {session.account} - {session.symbol} ({session.status})
                    </Checkbox>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export function filterTradeStatusList(tradeStatusList) {
  const sessionToTradeStatusMap = new Map();
  for (let tradeStatus of tradeStatusList) {
    const existingTradeStatus = sessionToTradeStatusMap.get(tradeStatus.sessionName);
    if (existingTradeStatus) {
      if (tradeStatus.updatedAt > existingTradeStatus.updatedAt) {
        sessionToTradeStatusMap.set(tradeStatus.sessionName, tradeStatus);
      }
    } else {
      sessionToTradeStatusMap.set(tradeStatus.sessionName, tradeStatus);
    }
  }
  return [...sessionToTradeStatusMap.values()];
}

export const tradeStatusToColor = {
  trading: "green",
  monitoring: "green",
  positionClosed: "blue",
  paused: "orange",
  closingPosition: "cyan",
  error: "red",
  marketClosed: "yellow",
};

export default GroupTable;
